/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * User Settings Operations
 * 
 * Handles user settings, walkthrough state, and velocity settings operations.
 */

import { UserSettingsData, IntegrationSettings, UISettings } from '@shared/types/user_settings';
import { ProductAppSettings, WalkthroughState, ProductVelocitySettings, ProductIssuesPageFilters } from '@shared/types/product-app-settings';
import { PlaybooksSettings } from '@shared/types/requests/user-settings';
import { authenticatedFetch } from '@shared/lib/api';
import { handleApiResponse } from '@shared/lib/apiResponse';

/**
 * Get user settings
 * SkillShop server endpoint - uses authenticatedFetch
 */
export async function getUserSettings(): Promise<UserSettingsData | null> {
  try {
    const response = await authenticatedFetch('/api/settings/users');
    return await handleApiResponse<UserSettingsData>(response);
  } catch (error) {
    console.error('[SkillShop Service] Error fetching user settings:', error);
    return null;
  }
}

/**
 * Update integration settings
 * SkillShop server endpoint - uses authenticatedFetch
 */
export async function updateIntegrationSettings(settings: IntegrationSettings): Promise<UserSettingsData | null> {
  try {
    const response = await authenticatedFetch('/api/settings/users/category', {
      method: 'PUT',
      body: {
        category: 'integrationSettings',
        settings,
      },
    });
    return await handleApiResponse<UserSettingsData>(response);
  } catch (error) {
    console.error('[SkillShop Service] Error updating integration settings:', error);
    return null;
  }
}

/**
 * Save product app settings
 */
export async function saveProductAppSettings(
  productSettings: ProductAppSettings,
  baseIntegration: IntegrationSettings | null,
): Promise<{ userSettings: UserSettingsData | null; integration: IntegrationSettings | null }> {
  const integrationSettings: IntegrationSettings = {
    ...(baseIntegration || {}),
    productApp: {
      ...productSettings,
      updatedAt: new Date().toISOString(),
    },
  };

  const updatedUserSettings = await updateIntegrationSettings(integrationSettings);
  const integration = updatedUserSettings?.integrationSettings || integrationSettings;

  return {
    userSettings: updatedUserSettings,
    integration,
  };
}

/**
 * Update velocity-specific settings (UI preferences, conflict resolution settings)
 * Merges with existing productApp settings
 */
export async function updateVelocitySettings(velocitySettings: ProductVelocitySettings): Promise<UserSettingsData | null> {
  try {
    // First get current settings to merge with
    const currentSettings = await getUserSettings();
    const currentProductApp = currentSettings?.integrationSettings?.productApp || {};
    
    const integrationSettings: IntegrationSettings = {
      ...(currentSettings?.integrationSettings || {}),
      productApp: {
        ...currentProductApp,
        velocitySettings: {
          ...currentProductApp.velocitySettings,
          ...velocitySettings,
        },
        updatedAt: new Date().toISOString(),
      },
    };
    
    return await updateIntegrationSettings(integrationSettings);
  } catch (error) {
    console.error('[SkillShop Service] Error updating velocity settings:', error);
    return null;
  }
}

/**
 * Update Issues page filter state in user settings.
 * Merges with existing productApp settings.
 */
export async function updateIssuesPageFilters(filters: ProductIssuesPageFilters): Promise<UserSettingsData | null> {
  try {
    const currentSettings = await getUserSettings();
    const currentProductApp = currentSettings?.integrationSettings?.productApp || {};
    const integrationSettings: IntegrationSettings = {
      ...(currentSettings?.integrationSettings || {}),
      productApp: {
        ...currentProductApp,
        issuesPageFilters: {
          ...currentProductApp.issuesPageFilters,
          ...filters,
        },
        updatedAt: new Date().toISOString(),
      },
    };
    return await updateIntegrationSettings(integrationSettings);
  } catch (error) {
    console.error('[SkillShop Service] Error updating issues page filters:', error);
    return null;
  }
}

/**
 * Save walkthrough state to user settings
 * SkillShop server endpoint - uses authenticatedFetch
 */
export async function saveWalkthroughState(
  walkthroughState: WalkthroughState,
  baseIntegration: IntegrationSettings | null
): Promise<{ success: boolean; error?: string }> {
  try {
    const currentProductSettings = baseIntegration?.productApp || {};
    
    const updatedProductSettings: ProductAppSettings = {
      ...currentProductSettings,
      walkthroughState,
      updatedAt: new Date().toISOString(),
    };

    const integrationSettings: IntegrationSettings = {
      ...(baseIntegration || {}),
      productApp: updatedProductSettings,
    };

    const response = await authenticatedFetch('/api/settings/users/category', {
      method: 'PUT',
      body: {
        category: 'integrationSettings',
        settings: integrationSettings,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to save walkthrough state (${response.status}): ${errorText}`);
    }

    console.log('[SkillShop Service] Walkthrough state saved successfully');
    return { success: true };
  } catch (error: any) {
    console.error('[SkillShop Service] Error saving walkthrough state:', error);
    return {
      success: false,
      error: error.message || 'Failed to save walkthrough state'
    };
  }
}

/**
 * Get walkthrough state from user settings
 * SkillShop server endpoint - uses authenticatedFetch
 */
export async function getWalkthroughState(): Promise<WalkthroughState | null> {
  try {
    const userSettings = await getUserSettings();
    return userSettings?.integrationSettings?.productApp?.walkthroughState || null;
  } catch (error) {
    console.error('[SkillShop Service] Error fetching walkthrough state:', error);
    return null;
  }
}

/**
 * Update UI settings
 * SkillShop server endpoint - uses authenticatedFetch
 */
export async function updateUiSettings(settings: UISettings): Promise<UserSettingsData | null> {
  try {
    const response = await authenticatedFetch('/api/settings/users/category', {
      method: 'PUT',
      body: {
        category: 'uiSettings',
        settings,
      },
    });
    return await handleApiResponse<UserSettingsData>(response);
  } catch (error) {
    console.error('[SkillShop Service] Error updating UI settings:', error);
    return null;
  }
}

/**
 * Update Playbooks page settings (UI preferences)
 * Merges with existing uiSettings
 */
export async function updatePlaybooksSettings(playbooksSettings: PlaybooksSettings): Promise<UserSettingsData | null> {
  try {
    // First get current settings to merge with
    const currentSettings = await getUserSettings();
    const currentUiSettings = currentSettings?.uiSettings || {};
    
    const uiSettings: UISettings = {
      ...currentUiSettings,
      playbooksSettings: {
        ...(currentUiSettings as UISettings).playbooksSettings,
        ...playbooksSettings,
      },
    };
    
    return await updateUiSettings(uiSettings);
  } catch (error) {
    console.error('[SkillShop Service] Error updating playbooks settings:', error);
    return null;
  }
}

